import React, { createContext, useContext, useCallback, useEffect, useState } from "react";
import { get_school_branding_api } from "../DAL/SchoolPortal/SchoolPortal";
import { get_instructor_onboarding_api } from "../DAL/Instructors/Instructors";
import { useGodMode } from "./GodModeContext";

// Single source of truth for "which school and modules is this session scoped
// to" — refetched live on mount instead of trusting whatever was cached in
// localStorage at login time. Fixes a real bug: a school admin who was
// already logged in before their school's modules changed (or before the
// backend started returning modules at all) would keep seeing a stale,
// possibly-wrong module set until they manually logged out and back in.
const SchoolContext = createContext(null);

export const useSchool = () => {
  const ctx = useContext(SchoolContext);
  if (!ctx) throw new Error("useSchool must be used within a SchoolProvider");
  return ctx;
};

const readCachedSchool = () => {
  try {
    const userData = JSON.parse(localStorage.getItem("user_data") || "{}");
    return userData?.flightSchool || null;
  } catch (e) {
    return null;
  }
};

export function SchoolProvider({ children }) {
  const { isGodMode, impersonatedSchool } = useGodMode();
  const [school, setSchool] = useState(() => (isGodMode ? impersonatedSchool : readCachedSchool()));

  useEffect(() => {
    if (isGodMode) {
      setSchool(impersonatedSchool || null);
      return;
    }

    let cancelled = false;
    get_school_branding_api().then((res) => {
      if (cancelled || !res?.flightSchool) return;
      setSchool(res.flightSchool);

      // Keep localStorage in sync so any code path reading it directly
      // (e.g. the pre-portal login redirect) also sees fresh data next time.
      try {
        const userData = JSON.parse(localStorage.getItem("user_data") || "{}");
        if (userData?.flightSchool) {
          userData.flightSchool = { ...userData.flightSchool, ...res.flightSchool };
          localStorage.setItem("user_data", JSON.stringify(userData));
        }
      } catch (e) {
        // non-fatal — worst case the next page load re-fetches
      }
    }).catch(() => {});

    return () => { cancelled = true; };
  }, [isGodMode, impersonatedSchool]);

  const modules = school?.modules || {};
  const hasModule = (name) => Boolean(modules[name]);
  // An independent instructor is a one-person school. Same machinery, but the
  // portal hides the parts that only make sense for an organisation (school
  // revenue, account manager, seat billing) and caps the team at staffLimit.
  const isIndependentInstructor = school?.schoolType === "independent_instructor";
  const staffLimit = school?.staffLimit ?? null;

  // Onboarding gate. Held here rather than in the guard so the guard and the
  // checklist read one answer, and so finishing a step can refresh both at
  // once. `null` means "not known yet" — the guard must not redirect on it,
  // or a slow request would bounce someone mid-navigation.
  const [onboarding, setOnboarding] = useState(null);

  // Asked for every school, not only groups. Who owes which steps — a school's
  // managers, its instructors, the front desk — is decided by the server
  // (web-backend utils/onboarding_steps.js), which answers "applies: false"
  // for anyone it should not gate.
  const refreshOnboarding = useCallback(async () => {
    try {
      const res = await get_instructor_onboarding_api();
      if (res?.code === 200 && res.data) setOnboarding(res.data);
      else setOnboarding({ applies: false, complete: true, steps: {} });
    } catch (e) {
      // Fail open — never trap someone behind a check that itself is broken.
      setOnboarding({ applies: false, complete: true, steps: {} });
    }
  }, []);

  useEffect(() => {
    if (!school) return;
    refreshOnboarding();
  }, [school, refreshOnboarding]);

  return (
    <SchoolContext.Provider value={{
      school, modules, hasModule, isIndependentInstructor, staffLimit,
      onboarding, refreshOnboarding,
    }}>
      {children}
    </SchoolContext.Provider>
  );
}
